export type Tier = {
  name: string;
  price: string;
  best: string;
  highlight?: boolean;
  features: string[];
};

export const pricingTiers: Tier[] = [
  {
    name: "Growth SEO",
    price: "$200",
    best: "Solo attorneys starting local",
    features: [
      "50 target keywords",
      "25 on-page optimizations / mo",
      "1 location · Local SEO + GBP",
      "Rank on ChatGPT & Gemini",
      "4 on-site blog posts / mo",
      "120+ backlinks / mo · 2 guest posts",
      "1 press release",
      "3-person team (Specialist · Manager · Writer)",
      "Reporting on rankings, calls and signed cases",
    ],
  },
  {
    name: "Advanced SEO",
    price: "$400",
    best: "Small firms expanding reach",
    features: [
      "100 target keywords",
      "50 on-page optimizations / mo",
      "2 locations · Local SEO + GBP",
      "Rank on ChatGPT & Gemini",
      "7 on-site blog posts / mo",
      "150+ backlinks / mo · 3 guest posts",
      "2 press releases",
      "3-person team",
      "Reporting on rankings, calls and signed cases",
    ],
  },
  {
    name: "Professional SEO",
    price: "$600",
    best: "Multi-attorney firms",
    highlight: true,
    features: [
      "200 target keywords",
      "80 on-page optimizations / mo",
      "3 locations · Local SEO + GBP",
      "Rank on ChatGPT + Gemini",
      "11 on-site blog posts / mo",
      "200+ backlinks / mo · 4 guest posts",
      "3 press releases · Website speed optimization",
      "4-person team",
      "Reporting on rankings, calls and signed cases",
    ],
  },
  {
    name: "Business Elite",
    price: "$800",
    best: "Established multi-office firms",
    features: [
      "300 target keywords",
      "100 on-page optimizations / mo",
      "4 locations · Local SEO + GBP",
      "Rank on all AI search engines",
      "17 on-site blog posts / mo",
      "300+ backlinks / mo · 5 guest posts",
      "4 press releases · Speed optimization",
      "5-person team",
      "Reporting on rankings, calls and signed cases",
    ],
  },
  {
    name: "Premium SEO",
    price: "$1,000",
    best: "Regional firms in competitive markets",
    features: [
      "400+ target keywords",
      "All-page on-page optimization",
      "All locations · Local SEO + GBP",
      "Rank on all AI search engines",
      "24 on-site blog posts / mo",
      "400+ backlinks / mo · 10 guest posts",
      "5 press releases · Speed optimization",
      "6-person team + Dedicated CRM",
      "Reporting on rankings, calls and signed cases",
    ],
  },
  {
    name: "Super Premium",
    price: "$1,200",
    best: "Enterprise & 25+ attorney firms",
    features: [
      "600+ target keywords",
      "All-page on-page optimization",
      "All locations · Full Local SEO",
      "Rank on all AI search engines",
      "29 on-site blog posts / mo",
      "700+ backlinks / mo · 20 guest posts",
      "6 press releases · Speed optimization",
      "7-person team + Dedicated CRM",
      "Reporting on rankings, calls and signed cases",
    ],
  },
];

export const startingPrice = pricingTiers[0].price;
export const lowestPlanPrice = startingPrice.replace(/[^0-9]/g, "");
export const highestPlanPrice = pricingTiers[pricingTiers.length - 1].price.replace(/[^0-9]/g, "");

function planPrice(name: string) {
  const plan = pricingTiers.find((tier) => tier.name === name);
  if (!plan) throw new Error(`Unknown SEO plan: ${name}`);
  return plan.price;
}

export const practicePricingTiers: Tier[] = [
  { name: "Growth SEO", price: planPrice("Growth SEO"), best: "Solo attorneys starting local", features: ["50 target keywords", "25 on-page optimizations / month", "1 location · Local SEO + Google Business Profile", "4 on-site blog posts / month"] },
  { name: "Professional SEO", price: planPrice("Professional SEO"), best: "Multi-attorney firms", highlight: true, features: ["200 target keywords", "80 on-page optimizations / month", "3 locations · Local SEO + Google Business Profile", "11 on-site blog posts / month"] },
  { name: "Premium SEO", price: planPrice("Premium SEO"), best: "Regional firms in competitive markets", features: ["400+ target keywords", "All-page on-page optimization", "All locations · Local SEO + Google Business Profile", "24 on-site blog posts / month"] },
];

// UK (GBP) variant for the /uk/* solicitor pages only. Same amounts and inclusions, UK spelling.
const ukLabels: Record<string, string> = {
  "Growth SEO": "Sole practitioners starting local",
  "Advanced SEO": "Small firms expanding their reach",
  "Professional SEO": "Multi-solicitor firms",
  "Business Elite": "Established multi-office firms",
  "Premium SEO": "Regional firms in competitive markets",
  "Super Premium": "Large firms with 25+ fee earners",
};
export const ukPricingTiers: Tier[] = pricingTiers.map((tier) => ({
  ...tier,
  price: tier.price.replace("$", "£"),
  best: ukLabels[tier.name],
  features: tier.features.map((f) => f.replace(/optimizations/g, "optimisations").replace(/optimization/g, "optimisation").replace(/optimize/g, "optimise")),
}));
